/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useRef } from 'react';
import { Platform, RefreshControl, ScrollView, StyleSheet, Text, TouchableOpacity, View, type LayoutChangeEvent } from 'react-native';
import { styles } from './ReportsScreen.styles';
import { spacing } from '../../shared/theme/spacing';
import type { ReportsScreenViewModel } from './useReportsScreen';
import {
  formatReportsOperacionDataSources,
  getReportsDateRange,
  isSalesTimeBandsApplicable,
} from './reportsScreen.utils';
import { ReportsCajaTurnosSummarySection } from './components/ReportsCajaTurnosSummarySection';
import { ReportsBlockHeading } from './components/ReportsBlockHeading';
import { ReportsDataSourceFooter } from './components/ReportsDataSourceFooter';
import { ReportsPeriodFilterPanel } from './components/ReportsPeriodFilterPanel';
import { RetailCounterReportsBand } from './components/RetailCounterReportsBand';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { ticketColors } from '../../shared/theme/ticketColors';
import { ReportsMetricTile } from './components/ReportsMetricTile';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { useReportsVentasLayout } from './reportsVentasLayout';
import { ReportsFiscalMonotributoSection } from '../../fiscal/public-api';
import { ComplaintsStatsSection } from './components/ComplaintsStatsSection';
import { ChannelBreakdownSection } from './components/ChannelBreakdownSection';
import { CatalogViewsSection } from './components/CatalogViewsSection';
import { StoreRatingStatsSection } from './components/StoreRatingStatsSection';
import { OnlineFunnelSection } from './components/OnlineFunnelSection';
import { ReportsStockMermaSection } from './components/ReportsStockMermaSection';
import { ReportsModifierConsumptionSection } from './components/ReportsModifierConsumptionSection';
import { ReportsKitchenProductivitySection } from './components/ReportsKitchenProductivitySection';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useGlobalFeatureFlags } from '../../shell/providers/GlobalFeatureFlagsProvider';
import { isGlobalFeatureOn } from '../../shell/feature-flags/globalFeatureFlagsService';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { formatMoney } from '../../shared/format/public-api';
import { ReportsTreasuryPeriodSection } from './components/ReportsTreasuryPeriodSection';

function formatLastDashboardLoadAt(iso: string | null | undefined): string {
  if (!iso) return 'Actualización pendiente';
  const label = formatLocalDateTime(iso);
  if (label === '—') return 'Actualización pendiente';
  return `Actualizado: ${label}`;
}

function formatPendingAge(iso: string | null | undefined): string {
  if (!iso) return 'Sin pendientes viejos';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return 'Fecha no válida';
  const hours = Math.floor(ms / 3600000);
  if (hours < 24) return `La más vieja: hace ${hours} h`;
  return `La más vieja: hace ${Math.floor(hours / 24)} días`;
}

type OperacionSectionKey =
  | 'local'
  | 'revisar'
  | 'tesoreria'
  | 'gastos'
  | 'caja'
  | 'stock'
  | 'merma'
  | 'cocina'
  | 'adicionales'
  | 'fiscal'
  | 'tienda';

export function ReportsOperationView({ vm }: { vm: ReportsScreenViewModel }) {
  const { layoutWide } = useReportsVentasLayout();
  const { entitlements } = usePlanEntitlements();
  const fiscalEnabledInPlan = entitlements?.fiscal_enabled === true;
  const statItemStyle = layoutWide
    ? [styles.reportsDenseListStatItem, styles.reportsOperacionStatItemWide]
    : styles.reportsDenseListStatItem;
  const tileLayout = layoutWide ? 'wide' : 'default';
  const periodDataLoading = vm.loading;
  const periodRange = getReportsDateRange(
    new Date(),
    vm.dateRangePreset,
    vm.dateRangePreset === 'custom' ? { from: vm.customRangeFrom, to: vm.customRangeTo } : null
  );
  const bandsApplicable = isSalesTimeBandsApplicable(periodRange);
  const { flags } = useGlobalFeatureFlags();
  const menuModifiersEnabled = useHasBusinessCapability('menu_modifiers');
  const kitchenTicketsEnabled = useHasBusinessCapability('kitchen_tickets');
  const expensesModuleEnabled = isGlobalFeatureOn(flags, 'expenses_module');
  const publicStoreFeaturesEnabled = usePublicStoreFeaturesVisible();
  const expensesSummary = vm.expensesSummary;

  const pendingReviewCount = vm.posExceptionSummary
    ? vm.posExceptionSummary.paymentPendingCount +
      vm.posExceptionSummary.fiscalPendingCount +
      vm.posExceptionSummary.syncFailedCount +
      vm.posExceptionSummary.mpQrPendingCount
    : 0;

  // Rail de secciones (solo wide): en web se usa scrollIntoView por nativeID
  // (resuelve el scroller real cualquiera sea); en nativo, scrollTo al y-offset
  // capturado con onLayout.
  const scrollRef = useRef<ScrollView | null>(null);
  const sectionOffsets = useRef<Partial<Record<OperacionSectionKey, number>>>({});
  const anchorProps = (key: OperacionSectionKey) => ({
    collapsable: false,
    nativeID: `informes-operacion-${key}`,
    onLayout: (e: LayoutChangeEvent) => {
      sectionOffsets.current[key] = e.nativeEvent.layout.y;
    },
  });
  const scrollToSection = (key: OperacionSectionKey) => {
    if (Platform.OS === 'web') {
      document
        .getElementById(`informes-operacion-${key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    const y = sectionOffsets.current[key];
    if (y == null) return;
    scrollRef.current?.scrollTo({ y: Math.max(0, y - spacing.sm), animated: true });
  };

  useEffect(() => {
    if (!vm.isOperacionSection) return;
    const anchor = vm.consumeOperacionAnchor?.();
    if (anchor === 'tesoreria') {
      scrollToSection('tesoreria');
    }
  }, [vm.isOperacionSection, vm.activeSection]);

  const railItems: Array<{ key: OperacionSectionKey; label: string; badge?: number }> = [
    { key: 'local', label: 'Hoy en el local' },
    { key: 'revisar', label: 'Para revisar', badge: pendingReviewCount > 0 ? pendingReviewCount : undefined },
    ...(vm.canShowTreasuryBlock ? [{ key: 'tesoreria' as const, label: 'Tesorería' }] : []),
    ...(expensesModuleEnabled && expensesSummary ? [{ key: 'gastos' as const, label: 'Gastos' }] : []),
    { key: 'caja', label: 'Caja y turnos' },
    { key: 'stock', label: 'Stock y alertas' },
    ...(vm.canInventory ? [{ key: 'merma' as const, label: 'Merma' }] : []),
    ...(kitchenTicketsEnabled ? [{ key: 'cocina' as const, label: 'Cocina' }] : []),
    ...(vm.canInventory && menuModifiersEnabled ? [{ key: 'adicionales' as const, label: 'Adicionales' }] : []),
    ...(fiscalEnabledInPlan ? [{ key: 'fiscal' as const, label: 'Fiscal' }] : []),
    ...(publicStoreFeaturesEnabled ? [{ key: 'tienda' as const, label: 'Tienda online' }] : []),
  ];

  const scrollContent = (
    <>
      <ReportsPeriodFilterPanel
        vm={vm}
        disabled={periodDataLoading}
        presetAccessibilityPrefix="Período operación"
      />

      <RetailCounterReportsBand
        externalServiceSummary={vm.externalServiceSummary}
      />

      <View {...anchorProps('local')}>
      <PaperSheet padded style={styles.kpiSheet} testID="informes.kpi.sheet">
        <Text style={styles.kpiSheetTitle}>El local</Text>
        <Text style={styles.kpiSheetHint}>{formatLastDashboardLoadAt(vm.lastDashboardLoadAt)}{vm.refreshing ? ' · actualizando…' : ''}</Text>
        <View style={styles.healthBadgeWrap}>
          <View style={[styles.healthBadge, { backgroundColor: vm.branchHealth.color }]}>
            <Text style={styles.healthBadgeText} numberOfLines={2}>
              {vm.branchHealth.message}
            </Text>
          </View>
        </View>
        <View style={styles.ventasIndicatorsGrid}>
          <ReportsMetricTile
            layout={tileLayout}
            label="Caja"
            value={vm.cashSummary?.activeSession ? 'Abierta' : 'Sin abrir'}
            sub={vm.cashSummary?.activeSession
              ? `Fondo inicial ${formatMoney(vm.cashSummary.activeSession.openingAmount)}`
              : `Efectivo vendido hoy ${formatMoney((vm.cashSummary?.todayCashTotal ?? 0))}`}
          />
          <ReportsMetricTile
            layout={tileLayout}
            label="Ventas hoy"
            value={`${formatMoney((vm.todaySalesSnapshot?.salesTotal ?? 0))}`}
            valueTone="emphasis"
            sub={`${vm.todaySalesSnapshot?.salesCount ?? 0} ventas · Fiscal ${formatMoney(vm.todaySalesFiscalTotal)} · No fiscal ${formatMoney(vm.todaySalesNonFiscalTotal)}`}
          />
        </View>
      </PaperSheet>
      </View>

      <View {...anchorProps('revisar')}>
      <PaperSheet padded>
        <Text style={styles.reportsDenseBlockTitle}>Ventas para revisar</Text>
        {vm.posExceptionSummary ? (
          <>
            <Text style={styles.caption}>Del período, con cobro, fiscal o sincronización pendiente.</Text>
            <View style={[styles.reportsDenseListStatGrid, layoutWide && styles.reportsOperacionStatGridWide]}>
              <View style={statItemStyle}>
                <Text style={styles.reportsDenseListStatItemLabel}>Pagos pendientes</Text>
                <Text style={styles.reportsDenseListStatItemValue}>{vm.posExceptionSummary.paymentPendingCount}</Text>
                <Text style={styles.caption}>{formatMoney(vm.posExceptionSummary.paymentPendingAmount)}</Text>
              </View>
              <View style={statItemStyle}>
                <Text style={styles.reportsDenseListStatItemLabel}>Fiscal pendiente</Text>
                <Text style={styles.reportsDenseListStatItemValue}>{vm.posExceptionSummary.fiscalPendingCount}</Text>
                <Text style={styles.caption}>{formatMoney(vm.posExceptionSummary.fiscalPendingAmount)}</Text>
              </View>
              <View style={statItemStyle}>
                <Text style={styles.reportsDenseListStatItemLabel}>Error al sincronizar</Text>
                <Text style={styles.reportsDenseListStatItemValue}>{vm.posExceptionSummary.syncFailedCount}</Text>
                <Text style={styles.caption}>{vm.posExceptionSummary.syncPendingCount} en cola</Text>
              </View>
            </View>
            <View style={[styles.reportsDenseListFieldRow, { marginTop: spacing.xs }]}>
              <Text style={styles.reportsDenseListFieldLabel}>Mercado Pago QR sin confirmar</Text>
              <Text style={styles.reportsDenseListFieldValue}>
                {vm.posExceptionSummary.mpQrPendingCount} ventas · {formatMoney(vm.posExceptionSummary.mpQrPendingAmount)}
              </Text>
            </View>
            <View style={[styles.reportsDenseListFieldRow, { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs }]}>
              <Text style={styles.reportsDenseListFieldLabel}>Mercado Pago QR</Text>
              <Text style={[styles.reportsDenseListFieldValue, { color: vm.posExceptionSummary.mpPosConfigured ? ticketColors.ok : ticketColors.gold }]}>
                {vm.posExceptionSummary.mpPosConfigured ? 'Configurado' : 'No configurado'}
              </Text>
            </View>
            {!vm.posExceptionSummary.mpPosConfigured ? (
              <TouchableOpacity
                style={[styles.secondaryButton, { marginTop: spacing.sm, alignSelf: 'flex-start' }]}
                onPress={vm.handleOpenMercadoPagoSettings}
                accessibilityRole="button"
                accessibilityLabel="Configurar Mercado Pago QR"
              >
                <Text style={styles.secondaryButtonText}>Configurar Mercado Pago QR</Text>
              </TouchableOpacity>
            ) : null}
            <Text style={[styles.caption, { marginTop: spacing.xs }]}>{formatPendingAge(vm.posExceptionSummary.oldestPendingSaleAt)}</Text>
          </>
        ) : (
          <Text style={styles.caption}>Sin datos del servidor. Conectate y recargá para ver las ventas a revisar.</Text>
        )}
      </PaperSheet>
      </View>

      {vm.canShowTreasuryBlock ? (
        <View {...anchorProps('tesoreria')}>
          <ReportsBlockHeading label="Tesorería del período" />
          <ReportsTreasuryPeriodSection
            variant="detail"
            summary={vm.treasurySummary}
            loading={vm.treasuryLoading}
            error={vm.treasuryError}
            periodHint={vm.dateRangeLabel}
            paymentMethodNames={vm.paymentMethodNames}
            layoutWide={layoutWide}
            stale={periodDataLoading}
            onRetry={() => void vm.reloadTreasury({ force: true })}
          />
        </View>
      ) : null}

      {expensesModuleEnabled && expensesSummary ? (
        <View {...anchorProps('gastos')}>
          <ReportsBlockHeading label="Gastos del período" />
          <PaperSheet padded>
            <Text style={styles.reportsDenseBlockTitle}>Detalle de gastos</Text>
            <Text style={[styles.caption, { marginBottom: spacing.xs }]}>
              Los totales de plata del período están en Resumen → Plata del período.
            </Text>
            <View style={[styles.reportsDenseListStatGrid, layoutWide && styles.reportsOperacionStatGridWide]}>
              <View style={statItemStyle}>
                <Text style={styles.reportsDenseListStatItemLabel}>Gastos totales</Text>
                <Text style={styles.reportsDenseListStatItemValue}>
                  {formatMoney(expensesSummary.expensesTotal)}
                </Text>
              </View>
              <View style={statItemStyle}>
                <Text style={styles.reportsDenseListStatItemLabel}>Egresos de caja</Text>
                <Text style={styles.reportsDenseListStatItemValue}>
                  {formatMoney(expensesSummary.cashOutflowsTotal)}
                </Text>
              </View>
              <View style={statItemStyle}>
                <Text style={styles.reportsDenseListStatItemLabel}>Pendientes de pago</Text>
                <Text style={styles.reportsDenseListStatItemValue}>
                  {formatMoney(expensesSummary.unpaidTotal)}
                </Text>
              </View>
            </View>
            {expensesSummary.bySettlementMethod.length > 0 ? (
              <View style={styles.reportsDenseListSection}>
                <Text style={styles.reportsSheetBlockTitle}>Cómo las pagaste</Text>
                {expensesSummary.bySettlementMethod.map((row, idx) => (
                  <View key={row.method} style={[styles.reportsDenseListFieldRow, idx > 0 && { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs }]}>
                    <Text style={styles.reportsDenseListFieldLabel}>{row.method}</Text>
                    <Text style={styles.reportsDenseListFieldValue}>{formatMoney(row.total)}</Text>
                  </View>
                ))}
              </View>
            ) : null}
            {expensesSummary.byPaymentStatus.length > 0 ? (
              <View style={styles.reportsDenseListSection}>
                <Text style={styles.reportsSheetBlockTitle}>Por estado de pago</Text>
                {expensesSummary.byPaymentStatus.map((row, idx) => (
                  <View key={row.status} style={[styles.reportsDenseListFieldRow, idx > 0 && { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs }]}>
                    <Text style={styles.reportsDenseListFieldLabel}>{row.status}</Text>
                    <Text style={styles.reportsDenseListFieldValue}>{formatMoney(row.total)}</Text>
                  </View>
                ))}
              </View>
            ) : null}
            <View style={styles.reportsDenseListSection}>
              <Text style={styles.reportsSheetBlockTitle}>Vinculadas a un turno de caja</Text>
              <View style={styles.reportsDenseListFieldRow}>
                <Text style={styles.reportsDenseListFieldLabel}>Con turno de caja</Text>
                <Text style={styles.reportsDenseListFieldValue}>
                  {formatMoney(expensesSummary.linkedToCashSessionTotal)}
                </Text>
              </View>
              <View style={[styles.reportsDenseListFieldRow, { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs }]}>
                <Text style={styles.reportsDenseListFieldLabel}>Sin turno de caja</Text>
                <Text style={styles.reportsDenseListFieldValue}>
                  {formatMoney(expensesSummary.unlinkedToCashSessionTotal)}
                </Text>
              </View>
            </View>
          </PaperSheet>
        </View>
      ) : null}

      <View {...anchorProps('caja')} style={periodDataLoading ? styles.ventasStaleData : undefined}>
        <ReportsBlockHeading label="Caja y turnos" />
        <ReportsCajaTurnosSummarySection vm={vm} />
      </View>

      <View {...anchorProps('stock')}>
      <ReportsBlockHeading label="Stock y alertas" />

      <View style={layoutWide ? styles.reportsOperacionCardsRowWide : undefined}>
      <PaperSheet padded style={layoutWide ? styles.reportsOperacionCardWide : undefined}>
        <Text style={styles.reportsDenseBlockTitle}>Stock (esta sucursal)</Text>
        <Text style={styles.metric}>{vm.inventoryDashboard?.totalProducts ?? 0} productos</Text>
        <Text style={styles.caption}>Cobertura: {vm.inventoryDashboard?.coveragePercent ?? 0}% · Valorización a costo: {formatMoney((vm.inventoryDashboard?.totalValue ?? 0))}</Text>
        <View style={[styles.reportsDenseListFieldRow, { marginTop: spacing.xs }]}>
          <Text style={styles.reportsDenseListFieldLabel}>Bajo mínimo</Text>
          <Text style={[styles.reportsDenseListFieldValue, (vm.inventoryDashboard?.belowMin ?? 0) > 0 && { color: ticketColors.bad }]}>{vm.inventoryDashboard?.belowMin ?? 0}</Text>
        </View>
        <View style={[styles.reportsDenseListFieldRow, { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs }]}>
          <Text style={styles.reportsDenseListFieldLabel}>Por vencer (7 días)</Text>
          <Text style={[styles.reportsDenseListFieldValue, (vm.inventoryDashboard?.expiringSoon ?? 0) > 0 && { color: ticketColors.gold }]}>{vm.inventoryDashboard?.expiringSoon ?? 0}</Text>
        </View>
        <View style={[styles.reportsDenseListFieldRow, { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs }]}>
          <Text style={styles.reportsDenseListFieldLabel}>Stock negativo</Text>
          <Text style={[styles.reportsDenseListFieldValue, (vm.inventoryDashboard?.negative ?? 0) > 0 && { color: ticketColors.bad }]}>{vm.inventoryDashboard?.negative ?? 0}</Text>
        </View>
      </PaperSheet>

      {vm.customerModuleEnabled &&
        vm.customerSummary &&
        (vm.customerSummary.paymentsCount > 0 || vm.customerSummary.chargesCount > 0) ? (
        <PaperSheet padded style={layoutWide ? styles.reportsOperacionCardWide : undefined}>
          <Text style={styles.reportsDenseBlockTitle}>Clientes hoy</Text>
          <View style={styles.reportsDenseListFieldRow}>
            <Text style={styles.reportsDenseListFieldLabel}>Cobros</Text>
            <Text style={styles.reportsDenseListFieldValue}>{formatMoney(vm.customerSummary.paymentsTotal)} ({vm.customerSummary.paymentsCount})</Text>
          </View>
          <View style={[styles.reportsDenseListFieldRow, { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: ticketColors.rule, paddingTop: spacing.xs }]}>
            <Text style={styles.reportsDenseListFieldLabel}>Cargos a crédito</Text>
            <Text style={styles.reportsDenseListFieldValue}>{formatMoney(vm.customerSummary.chargesTotal)} ({vm.customerSummary.chargesCount})</Text>
          </View>
        </PaperSheet>
      ) : vm.customerModuleEnabled ? (
        <PaperSheet padded style={layoutWide ? styles.reportsOperacionCardWide : undefined}>
          <Text style={styles.reportsDenseBlockTitle}>Clientes hoy</Text>
          <Text style={styles.caption}>Sin movimientos de crédito en el día.</Text>
        </PaperSheet>
      ) : null}
      </View>
      </View>

      {vm.canInventory ? (
        <View {...anchorProps('merma')}>
          <ReportsBlockHeading label="Merma" />
          <ReportsStockMermaSection
            vm={vm}
            dateRangeLabel={vm.dateRangeLabel}
            periodDataLoading={periodDataLoading}
          />
        </View>
      ) : null}

      {kitchenTicketsEnabled ? (
        <View {...anchorProps('cocina')}>
        <ReportsKitchenProductivitySection
          from={periodRange.fromIso}
          to={periodRange.toIso}
          cashSessions={vm.branchCashSessions}
        />
        </View>
      ) : null}

      {vm.canInventory && menuModifiersEnabled ? (
        <View {...anchorProps('adicionales')}>
        <ReportsModifierConsumptionSection
          from={periodRange.fromIso}
          to={periodRange.toIso}
        />
        </View>
      ) : null}

      <View {...anchorProps('fiscal')}>
      <ReportsFiscalMonotributoSection
        branchId={vm.currentBranchId ?? null}
        fiscalEnabledInPlan={fiscalEnabledInPlan}
      />
      </View>

      {publicStoreFeaturesEnabled ? (
        <View {...anchorProps('tienda')}>
          <ReportsBlockHeading
            label="Tienda online"
            description="Canal, visitas, calificaciones y reclamos del período."
          />
          <ChannelBreakdownSection
            from={periodRange.fromIso}
            to={periodRange.toIso}
          />
          <OnlineFunnelSection
            from={periodRange.fromIso}
            to={periodRange.toIso}
          />
          <CatalogViewsSection
            from={periodRange.fromIso}
            to={periodRange.toIso}
          />
          <StoreRatingStatsSection
            from={periodRange.fromIso}
            to={periodRange.toIso}
          />
          <ComplaintsStatsSection
            from={periodRange.fromIso}
            to={periodRange.toIso}
          />
        </View>
      ) : null}

      <ReportsDataSourceFooter
        lastUpdatedAt={vm.lastDashboardLoadAt}
        dataSourceHint={
          formatReportsOperacionDataSources(vm.kpiSources, null, { salesTimeBandsApplicable: bandsApplicable }).includes('Sin red')
            ? 'Algunos datos son de esta compu'
            : undefined
        }
      />
    </>
  );

  const scrollView = (
    <ScrollView
      ref={scrollRef}
      style={[styles.reportsContentScroll, layoutWide ? { flex: 1, minWidth: 0, alignSelf: 'stretch' } : null]}
      contentContainerStyle={[styles.content, { flexGrow: 1, paddingBottom: spacing.xxl }]}
      refreshControl={<RefreshControl refreshing={vm.refreshing} onRefresh={vm.onRefresh} colors={[ticketColors.ink2]} tintColor={ticketColors.ink2} />}
    >
      {scrollContent}
    </ScrollView>
  );

  if (!layoutWide) return scrollView;

  return (
    <View style={styles.reportsOperacionWideRow}>
      <View style={styles.reportsOperacionRail} accessibilityLabel="Secciones de Operación">
        {railItems.map((item) => (
          <TouchableOpacity
            key={item.key}
            style={styles.reportsOperacionRailItem}
            onPress={() => scrollToSection(item.key)}
            accessibilityRole="button"
            accessibilityLabel={`Ir a ${item.label}`}
          >
            <Text style={styles.reportsOperacionRailItemText} numberOfLines={1}>
              {item.label}
            </Text>
            {item.badge != null ? (
              <View style={styles.reportsOperacionRailBadge}>
                <Text style={styles.reportsOperacionRailBadgeText}>{item.badge}</Text>
              </View>
            ) : null}
          </TouchableOpacity>
        ))}
      </View>
      {scrollView}
    </View>
  );
}
